'use client'

import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import ExplanationVideoContent from '@/Explanation/ExplanationContent'
import {
  ExplanationPageDto,
  ExplanationPageQuery,
  useExplanationPageQuery,
  useValidateExplanationUniqueCodeMutation,
  ValidateExplanationUniqueCodeStatus,
} from '@/generated/graphql'
import { useForm } from 'react-hook-form'
import { useStore } from '@/store'
import { ExplanationAccessCodeForm } from '@/Explanation/ExplanationAccessCodeForm'

type ExplanationPasswordProps = {
  title: string
  slug: string
  selectedFileIndex?: number | null
  requiresUniqueCode?: boolean
  productName?: string | null
  thumbnailSrc?: string | null
  iosInstallLink?: string | null
  androidInstallLink?: string | null
}

type ExplanationAccessCodeFormData = {
  accessCode: string
}

type ValidatedAccessCode = {
  code: string
  alreadyRedeemed: boolean
}

// Encapsulate fetching explanation content so submit handler only deals with validation flow
const useLoadExplanationPage = (slug: string) =>
  useMutation<ExplanationPageQuery, unknown, string | null | undefined>({
    mutationKey: ['loadExplanationPage', slug],
    mutationFn: password =>
      useExplanationPageQuery.fetcher({
        slug,
        password: password ?? undefined,
      })(),
  })

export default function ExplanationPassword({
  title,
  slug,
  selectedFileIndex,
  requiresUniqueCode = false,
  productName,
  thumbnailSrc,
  iosInstallLink,
  androidInstallLink,
}: ExplanationPasswordProps) {
  const resolvedIosLink = useMemo(
    () => iosInstallLink ?? null,
    [iosInstallLink]
  )
  const resolvedAndroidLink = useMemo(
    () => androidInstallLink ?? null,
    [androidInstallLink]
  )
  const [validatedAccessCode, setValidatedAccessCode] =
    useState<ValidatedAccessCode | null>(null)
  const userId = useStore(state => state.user?.id)
  const { mutateAsync: validateUniqueCode, isPending: isValidatingUniqueCode } =
    useValidateExplanationUniqueCodeMutation()
  // Handles both password and unique-code fetches; result stored in mutation data
  const {
    mutateAsync: loadExplanationPage,
    data: explanationData,
    isPending: isLoadingExplanationPage,
  } = useLoadExplanationPage(slug)

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ExplanationAccessCodeFormData>({
    defaultValues: {
      accessCode: '',
    },
  })

  const handleValidationError = (message: string) => {
    setError('root', {
      type: 'manual',
      message,
    })
  }

  const getUniqueCodeErrorMessage = (
    status?: ValidateExplanationUniqueCodeStatus | null,
    fallback?: string | null
  ) => {
    switch (status) {
      case ValidateExplanationUniqueCodeStatus.InvalidCode:
        return 'Sadly that code isn’t valid. Please try again.'
      case ValidateExplanationUniqueCodeStatus.NotActivated:
        return 'This code has not yet been activated. Please contact Vanishing Inc.'
      case ValidateExplanationUniqueCodeStatus.WrongProduct:
        return 'This code does not match this explanation.'
      case ValidateExplanationUniqueCodeStatus.ExplanationNotFound:
        return 'We couldn’t find an explanation for that code. Please double-check and try again.'
      default:
        return (
          fallback ||
          'We were unable to verify that code. Please double-check it and try again.'
        )
    }
  }

  const onSubmit = async (data: ExplanationAccessCodeFormData) => {
    clearErrors('root')
    setValidatedAccessCode(null)

    try {
      if (requiresUniqueCode) {
        const accessCode = data.accessCode.trim()
        const response = await validateUniqueCode({
          slug,
          code: accessCode,
        })

        const validation = response?.validateExplanationUniqueCode

        if (!validation) {
          handleValidationError(
            'We were unable to verify that code. Please try again.'
          )
          return
        }

        if (validation.requiresLogin && !userId) {
          handleValidationError(
            'Please log in to your Vanishing Inc. account to continue.'
          )
          return
        }

        if (validation.status !== ValidateExplanationUniqueCodeStatus.Valid) {
          handleValidationError(
            getUniqueCodeErrorMessage(validation.status, validation.message)
          )
          return
        }

        setValidatedAccessCode({
          code: accessCode,
          alreadyRedeemed: validation.alreadyRedeemed === true,
        })

        const explanationResponse = await loadExplanationPage(null)

        if (!explanationResponse?.explanationPage) {
          handleValidationError(
            'We were unable to load this explanation. Please try again.'
          )
        }
      } else {
        const result = await loadExplanationPage(data.accessCode)

        if (!result?.explanationPage) {
          handleValidationError('Incorrect access code')
        }
      }
    } catch (err) {
      handleValidationError(
        err instanceof Error
          ? err.message
          : 'We were unable to validate that code. Please try again.'
      )
    }
  }

  const explanationPage: ExplanationPageDto | null =
    explanationData?.explanationPage ?? null
  const isSubmitting = isValidatingUniqueCode || isLoadingExplanationPage

  if (explanationPage) {
    return (
      <ExplanationVideoContent
        explanationPage={explanationPage}
        selectedFileIndex={selectedFileIndex}
        productName={productName}
        iosInstallLink={resolvedIosLink ?? undefined}
        androidInstallLink={resolvedAndroidLink ?? undefined}
        accessCode={validatedAccessCode?.code}
        accessCodeAlreadyRedeemed={validatedAccessCode?.alreadyRedeemed}
      />
    )
  }

  return (
    <ExplanationAccessCodeForm
      heading={productName ?? 'Unlock your explanation'}
      title={title}
      thumbnailAlt={
        productName
          ? `${productName} video thumbnail`
          : 'Explanation video thumbnail'
      }
      thumbnailSrc={thumbnailSrc}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      inputPlaceholder={
        requiresUniqueCode ? 'Enter your unique code' : 'Enter password'
      }
      submitLabel={isSubmitting ? 'Validating...' : 'Submit password'}
      errors={
        Object.values(errors)
          .map(error => error?.message)
          .filter(Boolean) as string[]
      }
      inputProps={register('accessCode', {
        required: 'Access code is required',
      })}
    />
  )
}
